import React, { useEffect, useState } from 'react';

import { Loader } from '@crehana/ui';

import { CreditCardIssuerType } from 'Jsx/CheckoutV4/types';

import useStripeV2Lib from 'Jsx/CheckoutV4/shared/hooks/useStripeV2Lib';

import CardHolderInput, {
  CardHolderSate,
} from '../CardHolderInput/CardHolderInput';

import CreditCardNumberInput, {
  CreditCardNumberState,
} from './components/CreditCardNumberInput';
import CVVInput, { CVCState } from './components/CVVInput';
import ExpirationDateInput, {
  ExpirationDateState,
} from './components/ExpirationDateInput';
import AnimatedCard from './components/AnimatedCard';

export interface CreditCardState {
  isValid: boolean;
  number: string;
  expiration: string;
  cvc: string;
}

interface CreditCardInputProps {
  onCreditCardChange: (state: CreditCardState) => void;
  onCardHolderChange?: (state: CardHolderSate) => void;
  showAnimatedCard: boolean;
  showCardHolder: boolean;
  cardHolder?: string;
  onLoaded?: (loading: boolean) => void;
}

type Focus = 'number' | 'expiration' | 'cvc';

const CreditCardInput: React.FC<CreditCardInputProps> = ({
  onCreditCardChange,
  onCardHolderChange = () => {},
  showAnimatedCard = true,
  showCardHolder = true,
  cardHolder,
  onLoaded = () => {},
}) => {
  const [number, setNumber] = useState<CreditCardNumberState>();
  const [expiration, setExpiration] = useState<ExpirationDateState>();
  const [cvc, setCvc] = useState<CVCState>();
  const [issuer, setIssuer] = useState<CreditCardIssuerType>();
  const [focus, setFocus] = useState<Focus>('number');
  const { isLoadingStripeLib } = useStripeV2Lib();

  const handleNumberChange = (state: CreditCardNumberState) => {
    setNumber(state);
    if (state.isValid) setFocus('expiration');
  };

  const handleExpirationChange = (state: ExpirationDateState) => {
    setExpiration(state);
    if (state.isValid) setFocus('cvc');
  };

  const handleCVCChange = (state: CVCState) => {
    setCvc(state);
  };

  useEffect(() => {
    onLoaded(!isLoadingStripeLib);
  }, [isLoadingStripeLib, onLoaded]);

  useEffect(() => {
    onCreditCardChange({
      number: number?.number || '',
      expiration: expiration?.expiration || '',
      cvc: cvc?.cvc || '',
      isValid:
        (number?.isValid && expiration?.isValid && cvc?.isValid) || false,
    });
  }, [number, expiration, cvc, onCreditCardChange]);

  if (isLoadingStripeLib)
    return (
      <div className="flex justify-center w-full">
        <Loader size={36} />
      </div>
    );

  return (
    <div className="w-full mb-24">
      {showAnimatedCard && (
        <div className="hidden sm:block w-full mb-24" style={{ maxWidth: 360 }}>
          <AnimatedCard
            creditCardCVV={cvc}
            creditCardExpiry={expiration}
            creditCardNumber={number}
            creditCardIssuer={issuer}
          />
        </div>
      )}
      {showCardHolder && (
        <CardHolderInput
          onCardHolderChange={onCardHolderChange}
          cardHolder={cardHolder}
        />
      )}
      <div className="mb-24">
        <CreditCardNumberInput
          onCreditCardNumberChange={handleNumberChange}
          onIssuerChange={setIssuer}
        />
      </div>
      <div className="flex justify-between">
        <div className="w-7/12 mr-8 sm:1/12 sm:mr-16">
          <ExpirationDateInput
            onExpirationDateChange={handleExpirationChange}
            autoFocus={focus === 'expiration'}
          />
        </div>
        <div className="w-7/12 sm:1/12">
          <CVVInput
            onCVCChange={handleCVCChange}
            cardIssuer={issuer}
            autoFocus={focus === 'cvc'}
          />
        </div>
      </div>
    </div>
  );
};

export default CreditCardInput;
